"use client"

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Copy, Check, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { getReviewAccessLink } from '../service'

interface ReviewAccessModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  email: string
  reviewYear: number
}

export function ReviewAccessModal({
  open,
  onOpenChange,
  email,
  reviewYear,
}: ReviewAccessModalProps) {
  const [link, setLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleGetLink = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await getReviewAccessLink(email, reviewYear)
      if (result.success && result.link) {
        setLink(result.link)
      } else {
        setError(result.error || 'Failed to get link')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to get link')
    } finally {
      setLoading(false)
    }
  }

  const handleCopy = async () => {
    if (link) {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Access Your Review</DialogTitle>
          <DialogDescription>
            Save this link to access your yearly review later. 
            <br/> Link expires in 30 days. 
            <br/> Bind it to your account to keep it with possibility to return to it next year.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {!link ? (
            <>
              <p className="text-sm text-muted-foreground">
                Get a shareable link to access your review later.
              </p>
              <Button onClick={handleGetLink} disabled={loading} className="w-full">
                {loading ? 'Generating...' : 'Get Link'}
              </Button>
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <Input value={link} readOnly className="flex-1" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(link, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Your Review
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                💡 <a href="/auth/sign-up" className='underline'>Sign up</a> to access your year review anytime, from any device
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

